import {Store} from 'emoji-mart'
import React, {useCallback, useEffect} from 'react'
import {Lang} from 'yougile-lang'
import {Util} from 'yougile-util'
import {BgStyle, Color, IconButton, Size} from '@components/common/base/buttons'
import {useBoolean} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import dataNative from '@emoji-mart/data/sets/14/native.json'
import dataTwitter from '@emoji-mart/data/sets/14/twitter.json'
import Picker from '@emoji-mart/react'
import {EmojiUtils} from '@extensions/emoji-utils'
import {globals} from '@extensions/globals-base'
import {Menu} from '../../menu'

const DEFAULT_EMOJI_NAMES = [
  '+1',
  '-1',
  'clap',
  'slightly_smiling_face',
  'grinning',
  'confused',
  'scream',
  'tada',
  'heart',
  'rocket',
  'heavy_check_mark'
]
const DEFAULT_EMOJI_WEIGHT = Number.MAX_SAFE_INTEGER
;(() => {
  const index = Store.get('frequently') || {}
  for (let i = 0; i < DEFAULT_EMOJI_NAMES.length; ++i) {
    index[DEFAULT_EMOJI_NAMES[i]] = DEFAULT_EMOJI_WEIGHT - i
  }
  Store.set('frequently', index)
})()

const TwitterEmojiPath = '/img/emoji/twitter-icons.png'

interface Props {
  onSelect(symbol: string): void
  closeOnSelect?: boolean
  className?: string
  iconColor?: Color
  iconClassName?: string
  // to allow locking a parent panel when emoji picker is displayed
  onAfterToggle?: (visible: boolean) => void
  large?: boolean
}

export const EmojiText = (props: {value: string; className?: string}) => {
  const {value, className} = props
  return (
    <span className={className}>
      {EmojiUtils.parse(value.replace('❤', '❤️').replace('✔', '✔️'))}
    </span>
  )
}
const MemoPicker = React.memo(Picker, (_prev, _next) => true)

const EmojiPickerBase = React.memo((props: Props): JSX.Element => {
  const bem = Util.bemFrom('emoji-picker')
  const menuOpen = useBoolean(false)

  useEffect(() => {
    props.onAfterToggle?.(menuOpen.value)
  }, [menuOpen.value, props.onAfterToggle])

  const select = useCallback(
    (emoji: {native: string}) => {
      if (props.closeOnSelect) {
        menuOpen.turnOff()
      }
      props.onSelect(emoji.native)
    },
    [props.onSelect, props.closeOnSelect, menuOpen.turnOff]
  )

  const useNative = EmojiUtils.shouldUseNative()

  const iconBg = props.large
    ? {wrapperSize: 'big' as Size, bgStyle: 'message' as BgStyle}
    : {}

  return (
    <div className={cn(bem({active: menuOpen.value}), props.className)}>
      <IconButton
        icon="IconMoodHappy"
        size="small"
        color={props.iconColor}
        onClick={menuOpen.toggle}
        className={cn(bem('button'), props.iconClassName)}
        forcedState={menuOpen.value ? 'active' : undefined}
        {...iconBg}
      />
      {menuOpen.value && (
        <Menu
          className={bem('menu')}
          tabIndex={-1}
          onEscape={menuOpen.turnOff}
          zIndex={1000}
        >
          <div className={bem('overlay')} onClick={menuOpen.turnOff} />
          <MemoPicker
            getSpritesheetURL={useNative ? undefined : () => TwitterEmojiPath}
            data={useNative ? dataNative : dataTwitter}
            onEmojiSelect={select}
            i18n={Lang.msg.chat.emoji}
            set={useNative ? 'native' : 'twitter'}
            previewPosition="none"
            skinTonePosition="none"
            navPosition="top"
            searchPosition="top"
            emojiSize={20}
            emojiButtonSize={32}
            perLine={8}
            theme={globals.theme}
          />
        </Menu>
      )}
    </div>
  )
})

export const EmojiPicker = (props: Props) => {
  return <EmojiPickerBase {...props} />
}
